import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconClipboard } from './Icons';
import './alertToast.css';

const ICON_STYLES = {
  success: { bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.15)', color: '#22c55e', symbol: '✓' },
  error:   { bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.15)',  color: '#ef4444', symbol: '✕' },
  warning: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.15)', color: '#f59e0b', symbol: '⚠' },
  info:    { bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.15)',  color: '#3b82f6', symbol: 'ℹ' },
};

function AlertModal({ show, message, onClose, type = 'info' }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [show]);

  // Standard messages are toasts: they close by themselves when the countdown bar ends
  // (success/info 3.5 s, warning/error 7 s; hovering pauses it). Console output stays a modal.

  if (!show) return null;

  const isConsoleOutput = message && message.includes('\n') && message.length > 100 && type !== 'info';
  const style = ICON_STYLES[type] || ICON_STYLES.info;

  const handleCopy = () => {
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const overlayStyle = {
    // above every page modal (.modal-overlay is 9999): a confirm opened from inside a modal
    // (e.g. Restore Backup → Restore) must not appear behind it
    position: 'fixed', inset: 0, zIndex: 10050,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.5rem',
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.2s ease',
  };

  // ── RCON / console output ──────────────────────────────────────
  if (isConsoleOutput) {
    return createPortal(
      <div style={overlayStyle} onClick={onClose}>
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: '70%', maxHeight: '60vh', maxWidth: 900,
            background: 'var(--bg-elevated, #1a1a24)',
            border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
            borderRadius: 'var(--radius-lg, 12px)',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            transform: visible ? 'scale(1)' : 'scale(0.97)',
            transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '0.875rem 1.25rem',
            borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'rgba(255,255,255,0.02)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary, #e8e8ed)', letterSpacing: '0.02em', fontFamily: 'var(--font-display)' }}>RCON Output</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleCopy} style={{
                padding: '6px 14px', borderRadius: 'var(--radius-sm, 6px)', border: '1px solid var(--border-default, rgba(255,255,255,0.09))',
                background: 'rgba(255,255,255,0.04)', color: 'var(--text-secondary, #8b8b9e)', fontSize: '0.78rem',
                cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font-sans)', transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>{copied ? '✓ Copied' : <><IconClipboard size={12} /> Copy</>}</button>
              <button onClick={onClose} style={{
                padding: '6px 14px', borderRadius: 'var(--radius-sm, 6px)', border: '1px solid rgba(239,68,68,0.15)',
                background: 'rgba(239,68,68,0.06)', color: '#ef4444', fontSize: '0.78rem',
                cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font-sans)',
              }}>✕ Close</button>
            </div>
          </div>

          {/* Terminal */}
          <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg-input, #0e0e15)', padding: '1.25rem 1.5rem' }}>
            <pre style={{
              margin: 0, color: 'var(--accent-cyan, #06b6d4)', fontSize: '0.82rem',
              lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontFamily: 'var(--font-mono)',
            }}>{message}</pre>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── Standard message → bottom-right toast (non-blocking) ───────
  const ms = type === 'success' || type === 'info' ? 3500 : 7000;
  // Portalled to <body>: inside the page's transformed wrapper a z-index only counts within the
  // page, so a dialog portalled to <body> (match ActionModal) would cover the toast / confirm.
  return createPortal(
    <div className="al-toast-wrap" role="status" aria-live="polite">
      <div key={message} className={`al-toast${visible ? ' in' : ''}`} style={{ '--al-tone': style.color, '--al-ms': `${ms}ms` }}>
        <span className="al-ic">{style.symbol}</span>
        <span className="al-msg">{message}</span>
        <button type="button" className="al-x" aria-label={t('common.close', 'Close')} onClick={onClose}>×</button>
        <span className="al-bar" onAnimationEnd={() => onClose && onClose()} />
      </div>
    </div>,
    document.body
  );
}

function ConfirmModal({ show, message, onConfirm, onClose, confirmText = 'Confirm', cancelText = 'Cancel', type = 'warning' }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // Translate the default button labels; custom labels pass through untranslated.
  const confirmLabel = confirmText === 'Confirm' ? t('common.confirm', 'Confirm') : confirmText;
  const cancelLabel = cancelText === 'Cancel' ? t('common.cancel', 'Cancel') : cancelText;

  const confirmRef = useRef(null);

  useEffect(() => {
    if (show) requestAnimationFrame(() => { setVisible(true); if (confirmRef.current) confirmRef.current.focus(); });
    else setVisible(false);
  }, [show]);

  // Esc = cancel, Enter = confirm (focus starts on the confirm button)
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose && onClose(); }
      else if (e.key === 'Enter' && document.activeElement === confirmRef.current) { e.preventDefault(); onConfirm && onConfirm(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, onClose, onConfirm]);

  if (!show) return null;

  // Split "CANCEL MATCH? This cannot be undone." into a title ("Cancel match?") and a body.
  const text = String(message || '');
  const q = text.indexOf('?');
  let title = q > 0 && q < 90 ? text.slice(0, q + 1).trim() : '';
  const body = title ? text.slice(q + 1).trim() : text;
  if (title && title === title.toUpperCase() && /[A-Z]/.test(title)) title = title.charAt(0) + title.slice(1).toLowerCase();
  if (!title) title = t('common.areYouSure', 'Are you sure?');

  // Destructive actions get a red warning icon + red button; everything else blue.
  const danger = type === 'danger'
    || (/\b(delete|remove|cancel|forfeit|reset|stop|kick|revoke|wipe|clear|undone)\b/i.test(`${confirmText} ${text}`)
      && !/^cancel$/i.test(confirmText));
  const tone = danger ? '#ef4444' : '#3b82f6';

  return createPortal( // above dialogs portalled to <body> (see AlertModal)
    <div className={`cf-backdrop${visible ? ' in' : ''}`} onMouseDown={e => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className="cf-card" role="alertdialog" aria-modal="true" aria-labelledby="cf-title" style={{ '--cf-tone': tone }}>
        <div className="cf-head">
          <span className="cf-ic" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
              {danger
                ? <><path d="M12 3L2 20h20z" /><path d="M12 10v4M12 17h.01" /></>
                : <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 014.6 1.3c0 1.7-2.1 2.2-2.1 3.7M12 17.5h.01" /></>}
            </svg>
          </span>
          <div className="cf-text">
            <h3 className="cf-title" id="cf-title">{title}</h3>
            {body && <p className="cf-body">{body}</p>}
          </div>
        </div>
        <div className="cf-actions">
          <button type="button" className="cf-btn" onClick={onClose}>{cancelLabel}</button>
          <button type="button" className="cf-btn go" ref={confirmRef} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export { AlertModal, ConfirmModal };
